import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { SiteProvider, useIsPrivateSite } from './contexts/SiteContext';
import { AuthProvider } from './contexts/AuthContext';
import { LoyaltyProvider } from './contexts/LoyaltyContext';
import { EditModeProvider, useEditMode } from './contexts/EditModeContext';
import { SiteBootstrapGate } from './components/layout/SiteBootstrapGate';
import { PrivateRoute } from './components/auth/PrivateRoute';
import { CmsPage } from './pages/CmsPage';
import { CollectionListPage } from './pages/CollectionListPage';
import { CollectionEntryPage } from './pages/CollectionEntryPage';
import { PreviewPage } from './pages/PreviewPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { useParentMessages } from './hooks/useParentMessages';
import { TrackingHead } from './components/tracking/TrackingHead';

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  // Activate postMessage listener when running inside the Visual Builder iframe
  useParentMessages();

  const location = useLocation();
  useEffect(() => {
    if (typeof window.MAP === 'function') window.MAP('page');
  }, [location.pathname]);

  return (
    <Routes>
      {/* Auth routes — always public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Preview — always public */}
      <Route path="/preview/:token" element={<PreviewPage />} />

      {/* Collection routes — must come before /:slug */}
      <Route
        path={`/${COLLECTION_PREFIX}/:typeSlug`}
        element={
          <PrivateCmsRoute>
            <CollectionListPage />
          </PrivateCmsRoute>
        }
      />
      <Route
        path={`/${COLLECTION_PREFIX}/:typeSlug/:slug`}
        element={
          <PrivateCmsRoute>
            <CollectionEntryPage />
          </PrivateCmsRoute>
        }
      />

      {/* Homepage */}
      <Route
        path="/"
        element={
          <PrivateCmsRoute>
            <CmsPage />
          </PrivateCmsRoute>
        }
      />

      {/* CMS pages */}
      <Route
        path="/:slug"
        element={
          <PrivateCmsRoute>
            <CmsPage />
          </PrivateCmsRoute>
        }
      />

      {/* 404 / redirects */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Wraps CMS routes with PrivateRoute only when the site is configured as private.
 * Public sites and edit mode render children directly.
 */
function PrivateCmsRoute({ children }: { children: React.ReactNode }) {
  const isPrivateSite = useIsPrivateSite();
  const { isEditMode } = useEditMode();
  // Skip auth gate in edit mode — the builder has its own authorization
  if (!isPrivateSite || isEditMode) return <>{children}</>;
  return <PrivateRoute>{children}</PrivateRoute>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <BrowserRouter>
          <EditModeProvider>
            <SiteProvider>
              <TrackingHead />
              <AuthProvider>
                <LoyaltyProvider>
                  <SiteBootstrapGate>
                    <AppRoutes />
                  </SiteBootstrapGate>
                </LoyaltyProvider>
              </AuthProvider>
            </SiteProvider>
          </EditModeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
